import { Suspense, useEffect } from 'react'
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClientInstance } from '@/lib/query-client'
import NavigationTracker from '@/lib/NavigationTracker'
import { pagesConfig } from './pages.config'
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import PageNotFound from './lib/PageNotFound';
import { AuthProvider, useAuth } from '@/lib/AuthContext';
import UserNotRegisteredError from '@/components/UserNotRegisteredError';
import AdminRoute from '@/lib/AdminRoute';
import { PageErrorBoundary } from '@/components/ErrorBoundary';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Support from './pages/Support';
import Feedback from './pages/Feedback';

const { Pages, Layout, mainPage } = pagesConfig;
const mainPageKey = mainPage ?? Object.keys(Pages)[0];
const MainPage = mainPageKey ? Pages[mainPageKey] : <></>;

const PageLoader = () => (
  <div className="flex-1 flex items-center justify-center" role="status" aria-label="Loading application">
    <div className="w-8 h-8 border-4 border-white/20 border-t-cyan-500 rounded-full animate-spin"></div>
  </div>
);

const LayoutWrapper = ({ children, currentPageName }) => Layout ?
  // Outer boundary wraps the Layout SHELL (sidebar, banners, providers) so a
  // crash there degrades to a recoverable page instead of white-screening the
  // whole app via the root AppErrorBoundary. Inner boundary isolates the page.
  // Both pass currentPageName (not key) so PageErrorBoundary persists across
  // routes and auto-clears via componentDidUpdate — that is what catches a throw
  // during the previous page's unmount instead of escalating to the app boundary.
  <PageErrorBoundary currentPageName={`shell-${currentPageName}`}>
    <Layout currentPageName={currentPageName}>
      <PageErrorBoundary currentPageName={currentPageName}>
        <Suspense fallback={<PageLoader />}>{children}</Suspense>
      </PageErrorBoundary>
    </Layout>
  </PageErrorBoundary>
  : <PageErrorBoundary currentPageName={currentPageName}>
      <Suspense fallback={<PageLoader />}>{children}</Suspense>
    </PageErrorBoundary>;

const AuthenticatedApp = () => {
  const { isLoadingAuth, isLoadingPublicSettings, authError, navigateToLogin } = useAuth();

  // Move auth redirect into an effect — calling navigateToLogin() during render
  // is a side-effect and causes a double-redirect in React StrictMode.
  useEffect(() => {
    if (authError?.type === 'auth_required') {
      navigateToLogin();
    }
  }, [authError, navigateToLogin]);

  // Show loading spinner while checking app public settings or auth
  if (isLoadingPublicSettings || isLoadingAuth) {
    return (
      <div className="fixed inset-0 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-white/20 border-t-cyan-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  // Handle authentication errors
  if (authError) {
    if (authError.type === 'user_not_registered') {
      return <UserNotRegisteredError />;
    } else if (authError.type === 'auth_required') {
      // Redirect handled by useEffect above — render nothing while redirecting
      return null;
    } else {
      // Unknown auth error type — show a recovery UI instead of falling through
      // to normal route render (which would be broken/stale state)
      return (
        <div className="fixed inset-0 flex flex-col items-center justify-center gap-4 bg-[#0F172A]">
          <p className="text-white text-lg">Something went wrong. Please try signing in again.</p>
          <button
            className="px-4 py-2 bg-[#06B6D4] text-white rounded-lg hover:bg-[#0891B2] transition-colors"
            onClick={() => navigateToLogin()}
          >
            Sign in
          </button>
        </div>
      );
    }
  }

  // Render the main app
  return (
    <Routes>
      <Route path="/" element={
        <LayoutWrapper currentPageName={mainPageKey}>
          <MainPage />
        </LayoutWrapper>
      } />
      {Object.entries(Pages).map(([path, Page]) => {
        // Admin-only pages: double-enforced via AdminRoute.
        // Analytics self-scopes by user.email — it is a per-user page, NOT admin-only.
        const adminPages = ['DevPortal'];
        const isAdminPage = adminPages.includes(path);
        return (
          <Route
            key={path}
            path={`/${path}`}
            element={
              <LayoutWrapper currentPageName={path}>
                {isAdminPage ? (
                  <AdminRoute><Page /></AdminRoute>
                ) : (
                  <Page />
                )}
              </LayoutWrapper>
            }
          />
        );
      })}
      <Route
        path="/Feedback"
        element={
          <LayoutWrapper currentPageName="Feedback">
            <Feedback />
          </LayoutWrapper>
        }
      />
      <Route path="/TermsOfService" element={<TermsOfService />} />
      <Route path="/PrivacyPolicy" element={<PrivacyPolicy />} />
      <Route path="/Support" element={<Support />} />
      <Route path="*" element={<PageNotFound />} />
    </Routes>
  );
};


function App() {

  return (
    <AuthProvider>
      <QueryClientProvider client={queryClientInstance}>
        <Router>
          <NavigationTracker />
          <AuthenticatedApp />
        </Router>
      </QueryClientProvider>
    </AuthProvider>
  )
}

export default App